/** The readiness checks of what the page shows: the stored tour in view mode, the draft
 *  laid over it in edit mode — the band's node states and the checks list read the same
 *  list. */
import { useMemo } from 'react';

import { tableRows } from '@/features/records/collections/rows';
import { recordKey } from '@/features/records/draft/changeSet';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';

import { ALIAS_ENTITY, TOUR_ENTITY } from './catalog';
import { COLLECTION_BY_ENTITY, COLLECTION_BY_MEMBERSHIP } from './collections';
import { useLaunchPolicies, useRestechAccounts } from './queries';
import { readinessChecks, readinessInputOf, type ReadinessCheck } from './readiness';
import { useTourRecord } from './TourRecordContext';

const ALIASES = COLLECTION_BY_ENTITY.get(ALIAS_ENTITY)!;

export function useReadiness(): ReadinessCheck[] {
  const { detail } = useTourRecord();
  const { mode, optionRef } = useRecordEditor();
  const { state, baselineOf } = useDraft();
  const restechs = useRestechAccounts(detail.tenant?.id);
  const editing = mode === 'edit';
  const restechList = restechs.data;
  const tourRec = recordKey(TOUR_ENTITY, detail.id);
  // A policy the draft picked: its owner's enforcement comes from the picker list.
  const policyChanged = editing && Boolean(state.entries[tourRec]?.launch_policy);
  const policies = useLaunchPolicies(policyChanged ? detail.tenant?.id : null);
  const policyList = policies.data;
  return useMemo(() => {
    const aliasIds = tableRows(state, baselineOf, tourRec, ALIASES, editing)
      .filter((r) => !r.removed)
      .map((r) => r.id);
    const valueOf = (rec: string, path: string) => {
      const entry = editing ? state.entries[rec]?.[path] : undefined;
      if (entry) return entry.to;
      const b = baselineOf(rec, path);
      if (b !== undefined) return b;
      const entity = rec.slice(0, rec.lastIndexOf(':'));
      return COLLECTION_BY_ENTITY.get(entity)?.defaults[path] ?? (COLLECTION_BY_MEMBERSHIP.has(path) ? [] : undefined);
    };
    return readinessChecks(
      readinessInputOf(detail, {
        aliasIds,
        valueOf,
        restechs: restechList ? restechList.map((r) => ({ code: r.code, account: r.account })) : null,
        policyOf: (id) => {
          const p = policyList?.find((x) => x.id === id);
          return { name: p?.name ?? optionRef('launch_policy', id)?.label ?? null, enforced: p?.enforced ?? null };
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, state.version, editing, restechList, policyList, baselineOf, optionRef]);
}
